import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NAV_ITEMS, NAV_GROUPS, canSee, ROLE_LABELS } from "@/lib/roles";
import { Menu, X, LogOut } from "lucide-react";
import GlobalSearch from "@/components/GlobalSearch";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: NAV_ITEMS.filter(
      (item) =>
        group.items.includes(item.key) &&
        canSee(item, user?.role || "", user?.roles, user?.effective_capabilities),
    ),
  })).filter((group) => group.items.length);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div
      className="min-h-screen bg-[#FDFBF7] flex overflow-x-hidden"
      data-testid="dashboard-shell"
    >
      {/* SIDEBAR */}

      <aside
        data-testid="sidebar"
        className={
          "fixed lg:static z-40 inset-y-0 left-0 w-72 h-screen bg-[#14224A] text-[#FDFBF7]/85 flex flex-col overflow-hidden transform transition-transform lg:translate-x-0 " +
          (open ? "translate-x-0" : "-translate-x-full")
        }
      >
        <div className="h-20 px-6 flex items-center gap-3 border-b border-white/10">
          <img
            src="/dfa-logo.svg"

            alt="DFA Logo"

            className="w-11 h-11 rounded-full bg-white p-0.5 ring-1 ring-[#9DCD3F]/40"
          />

          <div>
            <div className="font-display tracking-wide text-white">
              DARUSSALAM
            </div>

            <div className="text-[#9DCD3F]">Foundation Academy</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-thin pb-20">
          {visibleGroups.map((group) => (
            <div key={group.key}>
              {group.label && (
                <div className="px-3 mb-2 text-xs uppercase tracking-wider text-[#9DCD3F]">
                  {group.label}
                </div>
              )}

              {group.items.map((item) => (
                <NavLink
                  key={item.key}

                  to={item.to}

                  end={item.to === "/app"}

                  onClick={() => setOpen(false)}

                  className={({ isActive }) =>
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all " +
                    (isActive
                      ? "bg-[#9DCD3F] text-[#1B2C5A] font-medium"
                      : "text-[#FDFBF7]/70 hover:bg-white/5")
                  }
                >
                  <item.icon className="w-4 h-4" />

                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>
      {open && <button type="button" aria-label="Close navigation" onClick={() => setOpen(false)} className="fixed inset-0 z-30 bg-black/40 lg:hidden" />}

      {/* MAIN AREA */}

      <div className="min-w-0 flex-1 flex flex-col min-h-screen">
        <header className="h-20 sticky top-0 z-20 bg-white/90 backdrop-blur border-b flex items-center px-3 sm:px-6 gap-3 sm:gap-4">
          <button
            className="lg:hidden"

            onClick={() => setOpen(!open)}
          >
            {open ? <X /> : <Menu />}
          </button>

          <div className="flex-1">
            <GlobalSearch />
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <div className="max-w-48 truncate font-medium">{user?.name}</div>

              <div className="text-xs text-gray-500">
                {user?.organisational_position || ROLE_LABELS[user?.role] || user?.role}
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-10 h-10 rounded-full bg-[#1B2C5A] text-[#9DCD3F]">
                  {user?.avatar_initials || "U"}
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => navigate("/app/settings")}>
                  Settings
                </DropdownMenuItem>

                <DropdownMenuItem onClick={doLogout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-x-hidden p-3 sm:p-6" data-testid="dashboard-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
